import React from 'react'
import styled from '@emotion/styled'
import { Input, Select, Space } from 'antd'
import { PortalArticleNoteStyle } from '../../../models/PortalArtigoModel'
import {
  PORTAL_HEX_COLOR_REGEX,
  PORTAL_NOTE_VARIANT_OPTIONS,
} from '../helpers/portal-editor-options'
import { PortalEditorField } from './PortalEditorField'

const defaultNoteStyle = {
  borderColor: '#FF6422',
  background:
    'linear-gradient(0deg,rgba(255,100,34,0.1),rgba(255,100,34,0.1)),linear-gradient(87.2deg,rgba(255,255,255,0)_54.43%,rgba(255,100,34,0.084)_89.24%)',
  textColor: '#122A41',
  strongColor: '#122A41',
  linkColor: '#FF6422',
}

interface PortalNoteStyleEditorProps {
  value?: PortalArticleNoteStyle
  onChange: (value?: PortalArticleNoteStyle) => void
}

export function PortalNoteStyleEditor({ value, onChange }: PortalNoteStyleEditorProps) {
  const noteStyle = value || {}
  const isCustom = noteStyle.variant === 'custom'
  const previewStyle = resolvePreviewStyle(noteStyle)

  const updateStyle = (patch: PortalArticleNoteStyle) => {
    onChange({
      ...noteStyle,
      ...patch,
    })
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <PortalEditorField label="Estilo da nota">
        <Select
          value={isCustom ? 'custom' : 'default'}
          placeholder="Padrão"
          options={PORTAL_NOTE_VARIANT_OPTIONS}
          onChange={(variant) => {
            if (variant === 'custom') {
              updateStyle({ variant: 'custom' })
              return
            }

            onChange(undefined)
          }}
        />
      </PortalEditorField>

      <NotePreview style={previewStyle.container}>
        Texto da nota com <strong style={previewStyle.strong}>negrito</strong> e{' '}
        <a style={previewStyle.link}>link</a>.
      </NotePreview>

      {isCustom && (
        <AdvancedFields open>
          <summary>Cores avançadas</summary>
          <HelperText>Deixe em branco para usar a cor padrão daquele item.</HelperText>
          <ColorGrid>
            <ColorField
              label="Borda"
              value={noteStyle.borderColor}
              placeholder="#FF6422"
              onChange={(borderColor) => updateStyle({ borderColor })}
            />
            <ColorField
              label="Fundo"
              value={noteStyle.backgroundColor}
              placeholder="#FF6422"
              onChange={(backgroundColor) => updateStyle({ backgroundColor })}
            />
            <ColorField
              label="Texto"
              value={noteStyle.textColor}
              placeholder="#122A41"
              onChange={(textColor) => updateStyle({ textColor })}
            />
            <ColorField
              label="Negrito"
              value={noteStyle.strongColor}
              placeholder="#122A41"
              onChange={(strongColor) => updateStyle({ strongColor })}
            />
            <ColorField
              label="Link"
              value={noteStyle.linkColor}
              placeholder="#FF6422"
              onChange={(linkColor) => updateStyle({ linkColor })}
            />
          </ColorGrid>
        </AdvancedFields>
      )}
    </Space>
  )
}

function ColorField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string
  value?: string
  placeholder: string
  onChange: (value?: string) => void
}) {
  const pickerValue = getSafeHex(value) || placeholder

  return (
    <PortalEditorField label={label}>
      <ColorInputRow>
        <ColorPicker
          type="color"
          value={pickerValue}
          onChange={(event) => onChange(event.target.value)}
        />
        <Input
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      </ColorInputRow>
    </PortalEditorField>
  )
}

function resolvePreviewStyle(noteStyle: PortalArticleNoteStyle) {
  const customBackgroundColor = getSafeHex(noteStyle.backgroundColor)
  const borderColor = getSafeHex(noteStyle.borderColor) || defaultNoteStyle.borderColor
  const textColor = getSafeHex(noteStyle.textColor) || defaultNoteStyle.textColor
  const strongColor = getSafeHex(noteStyle.strongColor) || defaultNoteStyle.strongColor
  const linkColor = getSafeHex(noteStyle.linkColor) || defaultNoteStyle.linkColor

  return {
    container: {
      borderColor,
      background: customBackgroundColor
        ? createNoteGradient(customBackgroundColor)
        : defaultNoteStyle.background,
      color: textColor,
    },
    strong: {
      color: strongColor,
    },
    link: {
      color: linkColor,
    },
  }
}

function createNoteGradient(color: string) {
  const rgb = hexToRgb(color)
  if (!rgb) return defaultNoteStyle.background

  return `linear-gradient(0deg, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)), linear-gradient(87.2deg, rgba(255, 255, 255, 0) 54.43%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.09) 89.24%)`
}

function getSafeHex(value?: string) {
  if (!value) return undefined
  const trimmedValue = value.trim()
  return PORTAL_HEX_COLOR_REGEX.test(trimmedValue) ? trimmedValue : undefined
}

function hexToRgb(hex: string) {
  const normalizedHex = hex.replace('#', '')
  const fullHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalizedHex

  const value = Number.parseInt(fullHex, 16)
  if (!Number.isFinite(value)) return null

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  }
}

const AdvancedFields = styled.details`
  width: 100%;
  color: #595959;

  summary {
    cursor: pointer;
    color: #595959;
    font-weight: 600;
    margin-bottom: 8px;
  }
`

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const HelperText = styled.div`
  margin-bottom: 10px;
  color: #8c8c8c;
  font-size: 12px;
`

const NotePreview = styled.div`
  width: 100%;
  padding: 16px;
  border: 1.5px solid;
  border-radius: 6px;
  line-height: 24px;

  strong {
    font-weight: 700;
  }

  a {
    font-weight: 600;
    text-decoration: underline;
  }
`

const ColorInputRow = styled.div`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr);
  gap: 8px;
  align-items: center;
`

const ColorPicker = styled(Input)`
  width: 44px;
  padding: 2px;
`
